import { cn } from '@/lib/utils';

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const toneClass: Record<Tone, string> = {
  success: 'bg-[var(--success)] shadow-[0_0_0_2px_color-mix(in_oklab,var(--success)_18%,transparent)]',
  warning: 'bg-[var(--warning)] shadow-[0_0_0_2px_color-mix(in_oklab,var(--warning)_18%,transparent)]',
  danger: 'bg-destructive shadow-[0_0_0_2px_color-mix(in_oklab,var(--destructive)_18%,transparent)]',
  info: 'bg-primary shadow-[0_0_0_2px_color-mix(in_oklab,var(--primary)_18%,transparent)]',
  neutral: 'bg-muted-foreground/70 shadow-[0_0_0_2px_color-mix(in_oklab,var(--muted-foreground)_15%,transparent)]',
};

/**
 * Small status dot with a subtle halo. Pair with a text label for the
 * Stripe-style "● Active" / "● Sent" pattern.
 */
export function StatusDot({ tone = 'neutral', className }: { tone?: Tone; className?: string }) {
  return <span aria-hidden className={cn('inline-block size-1.5 shrink-0 rounded-full', toneClass[tone], className)} />;
}

/**
 * Status pill: dot + label, restrained background. Uses Stripe's "tinted text
 * on a soft tinted surface" approach instead of solid pill colors.
 */
export function StatusPill({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: React.ReactNode;
  className?: string;
}) {
  const wrap: Record<Tone, string> = {
    success: 'bg-[color-mix(in_oklab,var(--success)_10%,transparent)] text-[var(--success)]',
    warning: 'bg-[color-mix(in_oklab,var(--warning)_12%,transparent)] text-[var(--warning)]',
    danger: 'bg-[color-mix(in_oklab,var(--destructive)_10%,transparent)] text-destructive',
    info: 'bg-[color-mix(in_oklab,var(--primary)_10%,transparent)] text-primary',
    neutral: 'bg-muted text-muted-foreground',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        wrap[tone],
        className,
      )}
    >
      <StatusDot tone={tone} className="shadow-none" />
      {children}
    </span>
  );
}
